import React, { MouseEventHandler, ReactNode } from "react";
import styled, { css } from "styled-components";
import btnBack from "src/assets/images/btnBack.svg";

interface IButtonProps {
  variant?: string;
  className?: string;
  children: ReactNode;
  br?: string;
  color?: string;
  size?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  // 이 컴포넌트는 props 를 전개하지 않고 하나씩 넘긴다. 그래서 선언하지 않은 것은
  // 조용히 버려진다. aria-label 은 하이픈이 들어간 JSX 속성이라 타입 검사에서도
  // 걸리지 않아 더 조용하다 — 내용이 로고뿐인 버튼은 이름 없는 "버튼" 으로 읽힌다.
  "aria-label"?: string;
}

const Button = (props: IButtonProps) => {
  return (
    <ButtonSet variant={props.variant} className={props.className} br={props.br} color={props.color} size={props.size} onClick={props.onClick} disabled={props.disabled} aria-label={props["aria-label"]}>
      {props.children}
    </ButtonSet>
  );
};

// Button
// variant : text(default) | contained | outlined
// br? : 0px(default) | round : 20px | square : 4px
// color? : complete(완료) | secondary(취소) | white
const variantStyle = css<IButtonProps>`
  ${(props) => {
    switch (props.variant) {
      case "contained":
        return css`
          padding: 0 10px;
          background-color: ${({ theme }) => theme.color.main};
          width: 100%;
          height: 40px;
          /* line-height: 40px; */
          color: #fff;
          font-size: 16px;
          > svg {
            margin-right: 5px;
            vertical-align: text-bottom;
          }
        `;
      case "outlined":
        return css`
          padding: 0 10px;
          border: 1px solid ${({ theme }) => theme.color.main};
          background-color: #fff;
          width: 100%;
          height: 40px;
          /* line-height: 40px; */
          color: ${({ theme }) => theme.color.main};
          font-size: 16px;
        `;
      // default : text
      default:
        return css`
          color: ${({ theme }) => theme.color.subMain};
          font-size: 20px;
          font-family: "Poppins";
          font-weight: 400;
          &.btn-back {
            height: 28px;
            line-height: 28px;
            padding-left: 27px;
            color: #000;
            font-size: 20px;
            font-family: "Roboto";
            font-weight: 400;
            background: url(${btnBack}) no-repeat 0 0;
            &.no-ico {
              padding-left: 0;
              background: none;
            }
          }
          &.disabled {
            color: #818181;
          }
        `;
    }
  }}
`;

const shapeStyle = css<IButtonProps>`
  ${(props) => {
    switch (props.br) {
      case "round":
        return css`
          border-radius: 20px;
        `;
      case "square":
        return css`
          border-radius: 4px;
        `;
      // default : text
      default:
        return css`
          border-radius: 0;
        `;
    }
  }}
`;

const sizeStyle = css<IButtonProps>`
  ${(props) => {
    switch (props.size) {
      case "small":
        return css`
          width: auto;
          height: 24px;
          line-height: 22px;
          padding: 0 15px;
          font-size: 14px;
        `;
      case "medium":
        return css`
          width: auto;
          height: 28px;
          line-height: 26px;
          padding: 0 15px;
          font-size: 14px;
        `;
      // default
      default:
        return css``;
    }
  }}
`;

const colorStyle = css<IButtonProps>`
  ${(props) => {
    switch (props.color) {
      case "secondary":
        if (props.variant === "outlined") {
          return css`
            border: 1px solid ${({ theme }) => theme.color.lineSecondary};
            background-color: #fff;
            color: #60677d;
          `;
        }
        return css`
          background-color: ${({ theme }) => theme.color.bgPrimary};
          color: ${({ theme }) => theme.color.secondary};
        `;
      case "complete":
        return css`
          background-color: #00263e;
          color: #fff;
        `;
      case "active":
        return css`
          background-color: ${({ theme }) => theme.color.main};
          color: #fff;
        `;
      case "white":
        return css`
          background-color: #fff;
          color: ${({ theme }) => theme.color.main};
        `;
      case "subWhite":
        return css`
          border: 1px solid ${({ theme }) => theme.color.subMain};
          background-color: transparent;
          color: ${({ theme }) => theme.color.subMain};
        `;
      // default
      default:
        return css``;
    }
  }}
`;

const ButtonSet = styled.button<IButtonProps>`
  display: inline-block;
  border: 0;
  padding: 0;
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
  appearance: none;
  -webkit-appearance: none;
  font-size: 16px;
  background: none;
  vertical-align: middle;
  cursor: pointer;
  ${variantStyle}
  ${shapeStyle}
  ${sizeStyle}
  ${colorStyle}
`;

export default Button;
